import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { SimplePageLayout } from "../../components/layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPage = () => {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/blog/articles`);
        setArticles(response.data.articles);
      } catch (err) {
        console.error("Failed to fetch articles:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);
  
  return (
    <SimplePageLayout>
      <div className="max-w-4xl mx-auto">
        <span className="calli-badge mb-4">📝 Blog</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Blog Calli' : 'Calli Blog'}
        </h1>
        
        <p className="text-xl text-gray-600 mb-8">
          {language === 'fr' 
            ? "Conseils, études et réflexions sur l'éducation sans écran et le développement de l'enfant."
            : "Tips, studies, and thoughts on screen-free education and child development."}
        </p>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
          </div>
        ) : articles.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((article) => (
              <a
                key={article.slug}
                href={`/blog/${article.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:border-orange-200 hover:shadow-lg transition-all"
              >
                {article.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={article.image_url} 
                      alt={language === 'fr' ? article.title_fr : article.title_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="p-6">
                  <p className="text-sm text-orange-500 mb-2 font-medium">
                    {new Date(article.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                    {language === 'fr' ? article.title_fr : article.title_en}
                  </h3>
                  <p className="text-gray-600 line-clamp-3">
                    {language === 'fr' ? article.excerpt_fr : article.excerpt_en}
                  </p>
                  <div className="mt-4 flex items-center text-orange-500 font-medium">
                    {language === 'fr' ? "Lire l'article" : 'Read article'} →
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-orange-50 rounded-2xl">
            <p className="text-gray-600">
              {language === 'fr' ? 'Les articles arrivent bientôt !' : 'Articles coming soon!'}
            </p>
          </div>
        )}
      </div>
    </SimplePageLayout>
  );
};

export const BlogArticlePage = () => {
  const { language } = useLanguage();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(response.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  // Simple markdown-like rendering
  const renderContent = (text) => {
    if (!text) return null;
    
    return text.split('\n\n').map((paragraph, i) => {
      // Headers
      if (paragraph.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
      }
      if (paragraph.startsWith('### ')) {
        return <h3 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
      }
      
      // Lists
      if (paragraph.includes('\n- ')) {
        const items = paragraph.split('\n- ').filter(item => item.trim());
        return (
          <ul key={i} className="list-disc pl-6 space-y-2 my-4">
            {items.map((item, j) => (
              <li key={j} className="text-gray-700">{item.replace('- ', '')}</li>
            ))}
          </ul>
        );
      }
      
      // Bold and italic
      let content = paragraph
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');
      
      return (
        <p 
          key={i} 
          className="text-gray-700 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    });
  };

  if (loading) {
    return (
      <SimplePageLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        </div>
      </SimplePageLayout>
    );
  }

  if (error || !article) {
    return (
      <SimplePageLayout>
        <div className="max-w-3xl mx-auto text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'fr' ? 'Article non trouvé' : 'Article not found'}
          </h1>
          <a href="/blog" className="text-orange-500 hover:underline">
            {language === 'fr' ? '← Retour au blog' : '← Back to blog'}
          </a>
        </div>
      </SimplePageLayout>
    );
  }

  const title = language === 'fr' ? article.title_fr : article.title_en;
  const content = language === 'fr' ? article.content_fr : article.content_en;

  return (
    <SimplePageLayout>
      <article className="max-w-3xl mx-auto">
        {/* Back link */}
        <a href="/blog" className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-6">
          ← {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
        </a>

        {/* Header */}
        <header className="mb-8">
          <p className="text-orange-500 font-medium mb-2">
            {new Date(article.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            {title}
          </h1>
          {article.keywords && (
            <div className="flex flex-wrap gap-2">
              {article.keywords.split(',').map((keyword, i) => (
                <span key={i} className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                  {keyword.trim()}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Featured Image */}
        {article.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8">
            <img 
              src={article.image_url} 
              alt={title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {renderContent(content)}
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl text-center text-white">
          <h3 className="text-2xl font-bold mb-2">
            {language === 'fr' ? 'Découvrez Calli' : 'Discover Calli'}
          </h3>
          <p className="mb-4 opacity-90">
            {language === 'fr' 
              ? 'Le premier téléphone sans écran pour protéger votre enfant'
              : 'The first screenless phone to protect your child'}
          </p>
          <a
            href="/#preorder"
            className="inline-block bg-white text-orange-500 font-bold px-6 py-3 rounded-full hover:bg-orange-50 transition-colors"
          >
            {language === 'fr' ? 'Précommander Calli →' : 'Preorder Calli →'}
          </a>
        </div>

        {/* Share */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-gray-600 mb-4">{language === 'fr' ? 'Partagez cet article' : 'Share this article'}</p>
          <div className="flex gap-3">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-100 hover:bg-orange-100 rounded-full flex items-center justify-center text-gray-600 hover:text-orange-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-100 hover:bg-orange-100 rounded-full flex items-center justify-center text-gray-600 hover:text-orange-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-100 hover:bg-orange-100 rounded-full flex items-center justify-center text-gray-600 hover:text-orange-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </div>
        </div>
      </article>
    </SimplePageLayout>
  );
};
